import { createSelector } from 'reselect';

import { selectors } from '../../meta/selectors';
import { PureConnect, React } from '../../react';
import { DateTimeText } from '../../shared/date';
import { ICategory, IEntity, INote, INoteShared, IParticipant, IQuestion, IQuestionDB, isSharedNote, IUsergroup } from '../../types';
import { categoriesResource } from '../categories/resource';
import CategoryLabel from '../categoryLabel';
import { isNoteMissing } from '../locator/selectors';
import { notesEditor } from '../notes/resource';
import MiniMenu, { MiniMenuItem } from '../../react-toolbox-extended/mini-menu';
import cx from './style.less';
import TOGGLE_NOTEMENU from './actions/NOTES_TOGGLE_NOTEMENU';
import { notesSharedEditor } from '../noteshared/resource';
import { Btn } from '../../react-toolbox-redesign/button';
import { participantsResource } from '../../participant/resource';
import { usergroupResource } from '../../usergroup/resource';
import { useridResource } from '../../participantid/resource';
import NoteChatCreateControl from '../../note/notechat/editChat';
import { questionsEditor, questionsResource } from '../question/resource';

const lasttwochars = 2;

function reselect<TParam, TState, TResult>(fn: (param: TParam) => (state: TState) => TResult): (state: TState) => (param: TParam) => TResult {
    return (state: TState) => (param: TParam): TResult => fn(param)(state);
}

function mapParticipantName(participantId: string, allparticipants: IParticipant[]) {
    const participant = allparticipants.qWhere((item) => participantId === item.id);
    return (participant && participant.qAny() ? `${participant[0].name}, ` : '');
}

function mapGroupName(groupId: string, allparticipantgroups: IUsergroup[]) {
    const group = allparticipantgroups.qWhere((item) => parseInt(groupId) === item.id);
    return (group && group.qAny() ? `${group[0].name}, ` : '');
}

const notesEditorCategorySelector = createSelector(
    reselect<string, any, ICategory>(categoriesResource.selectors.itemById),
    notesEditor.selectors.item,
    (itemById, item) => item ? itemById(item.categoryId as any) : {} as ICategory || ({} as ICategory),
);

export const NoteDetailsNavigator = PureConnect(`NoteDetailsNavigator`)(
    (state) => ({
        creating: notesEditor.selectors.creating(state),
        updating: notesEditor.selectors.updating(state),
        category: notesEditorCategorySelector(state),
        currentUser: useridResource.selectors.items(state),
        allparticipants: participantsResource.selectors.items(state).qSelect((item) => item as IParticipant),
        allparticipantsgroups: usergroupResource.selectors.items(state).qSelect((item) => item as IUsergroup),
        isMissing: selectors.pageId(state) === notesEditor.selectors.item(state).pageId && isNoteMissing(state, notesEditor.selectors.item(state).id),
        itemNote: notesEditor.selectors.item(state) as INote,
        itemSharedNote: notesSharedEditor.selectors.item(state) as INoteShared,
        itemQuestion: questionsEditor.selectors.item(state) as IQuestionDB,
        showMiniMenu: state.notesReducer.showMiniMenu,
    }),
    (dispatch) => ({
        doDelete: (item: INote) => {
            dispatch(TOGGLE_NOTEMENU({ showMiniMenu: false, itemMiniMenu: undefined }));
            dispatch(notesEditor.actions.DELETE({ item }));
        },
        doEdit: (item: INote) => {
            dispatch(TOGGLE_NOTEMENU({ showMiniMenu: false, itemMiniMenu: undefined }));
            dispatch(notesEditor.actions.UPDATE({ item }));
        },
        toggleMiniMenu: (showMiniMenu: boolean, itemMiniMenu?: IEntity) => {
            dispatch(TOGGLE_NOTEMENU({ showMiniMenu, itemMiniMenu: undefined }));
        },
        cancelNoteRead: () => dispatch(notesEditor.actions.READ_CANCEL({})),
        cancelSharedNoteRead: () => dispatch(notesSharedEditor.actions.READ_CANCEL({})),
    }),
    ({ itemNote, itemSharedNote, allparticipants, allparticipantsgroups, category, currentUser, doEdit, cancelNoteRead, cancelSharedNoteRead, doDelete, showMiniMenu, toggleMiniMenu }) => {

        var item = itemSharedNote && !!isSharedNote(itemSharedNote)? itemSharedNote: itemNote;            
        const toggle = () => {
            showMiniMenu = !showMiniMenu;
            toggleMiniMenu(showMiniMenu, item);
        };

        const setMiniMenu = (showMenu: boolean) => {
            toggleMiniMenu(showMenu, item);
        };

        var participantid =
            currentUser.length > 0 ?
                currentUser[0].id :
                '';

        const itms: MiniMenuItem[] =
            [{
                name: 'Bewerken', iconName: 'edit', key: '1', functionitem: item, function: (item: INote) =>
                    doEdit(item),
            },
            {
                name: 'Verwijderen', iconName: 'delete', key: '2', functionitem: item, function: (item: INote) =>
                    doDelete(item),
            }];

        let sharedWithNoOne = !(item && item.noteParticipantsIDs && item.noteParticipantsIDs.length) &&
            !(item && item.noteParticipantGroupIDs && item.noteParticipantGroupIDs.length);

        return (
            <div className={cx('details', item.isquestion? 'wcag-invisible' : '')}>
                <section role="contentinfo" aria-label={'Sluit sectie'} className={cx('content-container')}>
                    <div className={cx('button-container')}>
                        <Btn
                            type="button"
                            className={cx('btn-icon-text_back', 'icon_arrow-back')}
                            aria-label={'Terug gaan'}
                            aria-haspopup={true}
                            tabIndex={0}
                            onClick={() => { setMiniMenu(false); !!isSharedNote(itemSharedNote)? cancelSharedNoteRead():cancelNoteRead(); }}
                            disabled={false}>
                            <span>Terug</span>
                        </Btn>
                        <div className={cx('menu', participantid !== item.userId ? 'wcag-invisible' : '')}>
                            <MiniMenu showMiniMenu={showMiniMenu}
                                toggleMiniMenu={() => toggle()}
                                items={itms}>
                            </MiniMenu>
                        </div>
                    </div>

                </section>
                <section className={cx('details-content-container', 'flyover')}>
                    <div className={cx('details-top-container')}>

                        <div className={cx('details-title-container')} onClick={() => setMiniMenu(false)}>
                            <div className={cx('icon-left-container')}>
                                <span className={cx('icon-container', item.isquestion ? 'question' : 'message')}></span>
                            </div>
                            <div className={cx('text-created-container')}>
                                <div className={cx('text-created-container-top')}>{item.isquestion ? 'TECHNISCHE VRAAG' : 'NOTITIE'}</div>
                                <div className={cx('text-created-container-bottom')}><DateTimeText date={item.created} /></div>
                            </div>
                        </div>
                    </div>
                    <div className={cx('middle-container')} onClick={() => setMiniMenu(false)}>
                        <div className={cx('made-by-container')}>
                            <div className={cx('text-created-container-top')}>Aangemaakt door</div>
                            <div className={cx('text-created-container-bottom')}>{mapParticipantName(item.userId, allparticipants)}</div>
                        </div>
                        <div className={cx('category-container')}>
                            <div className={cx('category-container-top')}>Categorie</div>
                            <div className={cx('category-container-bottom')}><CategoryLabel color={category && category.color ? category.color : '#efefef'} name={category && category.name ? category.name : ''} /></div>
                        </div>
                    </div>

                    {sharedWithNoOne ? null :
                        <div className={cx('middle-shared-container')} onClick={() => setMiniMenu(false)}>
                            <div className={cx('shared-with-label')}>Gedeeld met</div>
                            <div className={cx('list-of-shared')}>

                                {item && item.noteParticipantsIDs && item.noteParticipantsIDs.length ?
                                    <div className={cx('shared-div')}><span className={cx('icon-shared')}></span>
                                        {item.noteParticipantsIDs.map((itm) => mapParticipantName(itm, allparticipants)).join('').slice(0, -lasttwochars)}
                                    </div>
                                    : null
                                }
                                {item && item.noteParticipantGroupIDs && item.noteParticipantGroupIDs.length ?
                                    <div className={cx('shared_multiple-div')}><span className={cx('icon-shared_multiple')}></span>
                                        {item.noteParticipantGroupIDs.map((itm) => mapGroupName(itm, allparticipantsgroups)).join('').slice(0, -lasttwochars)}
                                    </div>
                                    : null
                                }

                            </div>
                        </div>
                    }

                    <div className={cx('bottom-container')} onClick={() => setMiniMenu(false)}>
                        <div className={cx('note-label')}>Notitie</div>
                        <span className={cx('note-text')}>{item.text}</span>
                    </div>
                    <NoteChatCreateControl />
                </section>
                
            </div>

        );
    });

interface IPropsDetailsModal {
    item: INote | INoteShared | IQuestion;
    itemQuestion: IQuestionDB;
}

export const NoteDetailsControl = PureConnect<any | IPropsDetailsModal>(`NoteDetailsControl`)(
    (state) => ({
        category: notesEditorCategorySelector(state),
        allparticipants: participantsResource.selectors.items(state).qSelect((item) => item as IParticipant),
        allparticipantsgroups: usergroupResource.selectors.items(state).qSelect((item) => item as IUsergroup),
        allQuestions: questionsResource.selectors.items(state),
    }),
    (dispatch) => ({
     }),
    ({ item, itemQuestion, allQuestions, allparticipants, allparticipantsgroups, category, koppel, ontkoppel }) => {
        let sharedWithNoOne = !(item && item.noteParticipantsIDs && item.noteParticipantsIDs.length) &&
            !(item && item.noteParticipantGroupIDs && item.noteParticipantGroupIDs.length);

        return (
            <div className={cx('details')}>
                <section role="contentinfo" aria-label={'Sluit sectie'} className={cx('content-container')}>
                    <div className={cx('details-top-container')} tabIndex={0}>
                        <div className={cx('details-title-container')}>
                            <span className={cx('title-text-action')}>{item.isquestion ? 'Technische vraag raadplegen' : 'Notitie raadplegen'}</span>
                            <div className={cx('icon-left-container')}>
                                <span className={cx('icon-container', item.isquestion ? 'question' : 'message')}></span>
                            </div>
                            <div className={cx('text-created-container')}>
                                <div className={cx('text-created-container-top')}>{item.isquestion ? 'TECHNISCHE VRAAG' : 'NOTITIE'}</div>
                                <div className={cx('text-created-container-bottom')}><DateTimeText date={item.created} /></div>
                            </div>
                        </div>
                    </div>
                </section>
                <section className={cx('details-content-container', 'modal')}>
                    <div className={cx('middle-container')}>
                        <div className={cx('made-by-container')}>
                            <div className={cx('text-created-container-top')}>Aangemaakt door</div>
                            <div className={cx('text-created-container-bottom')}>{mapParticipantName(item.userId, allparticipants)}</div>
                        </div>
                        <div className={cx('category-container')}>
                            <div className={cx('category-container-top')}>Categorie</div>
                            <div className={cx('category-container-bottom')}><CategoryLabel color={category && category.color ? category.color : '#efefef'} name={category && category.name ? category.name : ''} /></div>
                        </div>
                    </div>

                    {sharedWithNoOne ? null :
                        <div className={cx('middle-shared-container')}>
                            <div className={cx('shared-with-label')}>Gedeeld met</div>
                            <div className={cx('list-of-shared')}>

                                {item && item.noteParticipantsIDs && item.noteParticipantsIDs.length ?
                                    <div className={cx('shared-div')}><span className={cx('icon-shared')}></span>
                                        {item.noteParticipantsIDs.map((itm: number) => mapParticipantName(itm.toString(), allparticipants)).join('').slice(0, -lasttwochars)}
                                    </div>
                                    : null
                                }
                                {item && item.noteParticipantGroupIDs && item.noteParticipantGroupIDs.length ?
                                    <div className={cx('shared_multiple-div')}><span className={cx('icon-shared_multiple')}></span>
                                        {item.noteParticipantGroupIDs.map((itm: number) => mapGroupName(itm.toString(), allparticipantsgroups)).join('').slice(0, -lasttwochars)}
                                    </div>
                                    : null
                                }

                            </div>
                        </div>
                    }

                    <div className={cx('bottom-container')}>
                        <div className={cx('note-label')}>Notitie</div>
                        <span className={cx('note-text')}>{item.text}</span>
                    </div>                               
                    <NoteChatCreateControl />
                </section>

            </div>

        );
    });



// WEBPACK FOOTER //
// (webpack)-remove-block-loader??ref--5-0!./src/note/notes/details.tsx